import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, AlertTriangle } from 'lucide-react';

// Package limits (from backend packages.js)
const PACKAGE_LIMITS = {
  'postcard': { maxMessages: 1, maxEvents: 0, name: 'Postcard' },
  'start': { maxMessages: 5, maxEvents: 0, name: 'Start' },
  'family': { maxMessages: 20, maxEvents: 5, name: 'Family' },
  'family-plus': { maxMessages: 80, maxEvents: 20, name: 'Family+' },
  'jga': { maxMessages: 20, maxEvents: 1, name: 'JGA' },
  'jga-plus': { maxMessages: 20, maxEvents: 1, name: 'JGA+' },
  'demo': { maxMessages: 3, maxEvents: 2, name: 'Demo (30 min)' }
};

export default function DemoLimitsInfo({ user, messageCount = 0, eventCount = 0 }) {
  const [timeRemaining, setTimeRemaining] = useState(30); // 30 minutes
  const isDemoMode = user?.isDemo === true;  // Use JWT flag instead of localStorage
  const packageId = user?.plan || 'demo';
  const limits = PACKAGE_LIMITS[packageId] || PACKAGE_LIMITS['demo'];
  const demoExpiresAt = user?.demoExpiresAt || (Date.now() + 30 * 60 * 1000);  // Fallback to 30 min from now

  // Calculate remaining demo time
  useEffect(() => {
    const interval = setInterval(() => {
      const timeLeft = demoExpiresAt - Date.now();
      const remaining = Math.max(0, Math.ceil(timeLeft / 60000));  // Convert to minutes
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [demoExpiresAt]);

  if (!isDemoMode) return null;

  const messagePercent = (messageCount / limits.maxMessages) * 100;
  const eventPercent = (eventCount / limits.maxEvents) * 100;
  const isLowTime = timeRemaining < 5;
  const isExpired = timeRemaining === 0;

  return (
    <div className="demo-limits-info bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="text-blue-600" size={20} />
        <h3 className="text-lg font-bold text-blue-900">🎉 Demo-Modus Aktiv</h3>
        {isExpired && <AlertTriangle className="text-red-600 ml-auto" size={20} />}
      </div>

      {/* Time Remaining */}
      <div className={`mb-4 p-3 rounded flex items-center gap-2 ${isExpired ? 'bg-red-100 text-red-800' : isLowTime ? 'bg-orange-100 text-orange-800' : 'bg-white text-gray-800'}`}>
        <Clock size={18} />
        <span className="font-semibold">
          {isExpired 
            ? '⏰ Demo-Zeit abgelaufen - Daten werden gelöscht!' 
            : `⏱️ Demo endet in ${timeRemaining} Minuten`
          }
        </span>
      </div>

      {/* Limits Display */}
      <div className="grid grid-cols-2 gap-4">
        {/* Messages */}
        <div className="bg-white p-3 rounded border border-blue-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">📝 Nachrichten</span>
            <span className="text-xs font-bold text-blue-600">{messageCount}/{limits.maxMessages}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all" 
              style={{ width: `${Math.min(messagePercent, 100)}%` }}
            />
          </div>
          {messageCount >= limits.maxMessages && (
            <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ Limit erreicht!</p>
          )}
        </div>

        {/* Events */}
        <div className="bg-white p-3 rounded border border-purple-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">📅 Events</span>
            <span className="text-xs font-bold text-purple-600">{eventCount}/{limits.maxEvents}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-500 h-2 rounded-full transition-all" 
              style={{ width: `${Math.min(eventPercent, 100)}%` }}
            />
          </div>
          {eventCount >= limits.maxEvents && (
            <p className="text-xs text-red-600 mt-2 font-semibold">⚠️ Limit erreicht!</p>
          )}
        </div>
      </div>

      {/* Package Info */}
      <div className="mt-4 p-3 bg-gradient-to-r from-blue-100 to-purple-100 rounded text-sm text-gray-800">
        <p className="font-semibold mb-1">📦 Paket-Limits ({limits.name}):</p>
        <p className="text-xs">
          • Nachrichten: {limits.maxMessages} pro Demo  
          • Events: {limits.maxEvents} pro Demo
        </p>
      </div>

      {/* Info Message */}
      <p className="text-xs text-gray-600 mt-3 italic">
        ℹ️ Das ist eine 30-Minuten Demo-Session. Deine Daten sind hier nur temporär. Registriere dich für bleibenden Zugriff!
      </p>
    </div>
  );
}
